import { useCallback, useEffect, useMemo, useState } from "react";
import { AppId, FCX, ProductLogo } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useSocialThemes } from "@core/apiHooks/useSocialThemes";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { requestTrialByFeature } from "@actions/page";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import ConversationThemes from "./ConversationThemes";
import FoodiesExamplePosts from "./FoodiesExamplePosts";
import KeyTerms from "./KeyTerms";
import './FoodiesSaying.scss';

const COMPACT_THRESHOLD = 5;

const FoodiesSaying: FCX<{
    influencerTypeId?: number | null;
    isInfluencersView?: boolean;
}> = ({ influencerTypeId, isInfluencersView = false }) => {
    const dispatch = useDispatch();
    const {
        data: { themes, postsByTheme, keywordsByTheme, permissionLevel },
        isLoading,
    } = useSocialThemes(influencerTypeId);
    const [selectedThemeId, setSelectedThemeId] = useState<number | null>(null);

    useEffect(() => {
        setSelectedThemeId(null);
    }, [influencerTypeId]);

    const isBlocked = useMemo(
        () => permissionLevel !== PermissionLevelEnum.Full,
        [permissionLevel],
    );

    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('social'));
    }, [dispatch]);

    const activeThemeId = useMemo(
        () => selectedThemeId ?? themes[0]?.id ?? null,
        [selectedThemeId, themes],
    );

    const posts = useMemo(() => {
        if (activeThemeId === null) return [];
        const themePosts = postsByTheme[activeThemeId];
        return themePosts?.byEngagement ?? [];
    }, [activeThemeId, postsByTheme]);

    const keywords = useMemo(() => {
        if (activeThemeId === null) return [];
        return keywordsByTheme[activeThemeId] ?? [];
    }, [activeThemeId, keywordsByTheme]);

    const isCompact = useMemo(
        () => themes.length <= COMPACT_THRESHOLD,
        [themes],
    );

    if (isLoading) {
        return (
            <div className="FoodiesSaying is-loading" data-testid="blockFoodiesSaying">
                <div className="TrendingNow__spinner">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn("FoodiesSaying", isCompact && "is-compact", isBlocked && "is-blocked")}
            data-testid="blockFoodiesSaying"
        >
            <ConversationThemes
                data={themes}
                selectedId={activeThemeId}
                onSelect={setSelectedThemeId}
                isInfluencersView={isInfluencersView}
            />
            <FoodiesExamplePosts data={posts} isBlocked={isBlocked} selectedThemeId={activeThemeId}/>
            <KeyTerms data={keywords} isCompact={isCompact} selectedThemeId={activeThemeId}/>
            {isBlocked && (
                <PermissionOverlay
                    onContactUsClick={requestTrial}
                    testId="blockSocialListeningPermissionOverlay"
                >
                    <div>Get the full Datassential One experience -
                        upgrade now to access the extended Social Listening data!
                    </div>
                </PermissionOverlay>
            )}
        </div>
    );
};

export default FoodiesSaying;
